/**
 * Platform pointer policy (pure): classify down → pending; resolve move/up → effects.
 *
 * Intent table (hasSectors × hit × moved → up):
 * - no  + sector + !moved → activate sector
 * - no  + sector + moved  → noop (look)
 * - object + !moved → activate object (wins over sector paint)
 * - object + moved  → noop (look)
 * - yes + sector + !moved → replace [cell]
 * - yes + sector + moved  → marquee (engine owns paint after promote)
 * - yes + miss   + *      → clear
 * - yes + face   + *      → activate wall (look does not cancel)
 * - no  + void   + !moved → activate environment chrome
 * - no  + void   + moved  → noop (look)
 * - recordArmed | recording | cameraMoving → look only (no pending; click does not select)
 *   Shift on a trajectory point, axis, or handle still takes that gesture
 *
 * Trajectory mode (when `trajectory.active`, before the platform rows):
 * - playback / not the primary button → no gesture
 * - double-click on a point or handle → reset bend, no drag
 * - axis or handle → drag that control immediately
 * - point marker → pending; move → slide on the camera plane; up → select
 *   (engine deletes the point on that click while the pen is on)
 * - empty + draw + on the curve → insert on up, do not place on down
 * - empty + draw + floor hit → place the point on down, then bend on drag
 * - empty without draw, Alt, observe pick, or a floor miss → pending; move is ordinary
 *   look (drag orbits like the view cube; Shift turns the camera in place); up inserts,
 *   picks the object, or does nothing
 */

export type PlatformPointerCell = { x: number; z: number }

export type PlatformMeshHit = "miss" | "sector" | "face" | "opening"

export type PlatformActivateTarget =
  | { kind: "sector"; x: number; z: number }
  | { kind: "face"; faceKey: string; tile?: { faceKey: string; u: number; v: number } }
  | { kind: "object"; uid: string }

export type PathPointerHit =
  | { kind: "point"; index: number }
  | { kind: "axis"; index: number; axis: "x" | "y" | "z" }
  | { kind: "handle"; index: number; side: 1 | -1 }

export type TrajectoryPointerCtx = {
  active: boolean
  draw: boolean
  alt: boolean
  observeArmed: boolean
  doubleClick: boolean
  floorHit: boolean
  /** Ray is close enough to the curve to insert, same threshold as Alt-insert. */
  onCurve?: boolean
  hit: PathPointerHit | null
}

export type PathDownEffect = { type: "none" } | { type: "placePoint" } | { type: "resetTangent"; index: number }

export type PlatformPending =
  | { kind: "idleClear" }
  | { kind: "sectorPaint"; anchor: PlatformPointerCell; shift: boolean }
  | { kind: "activate"; target: PlatformActivateTarget; shift: boolean }
  | { kind: "activateEnvironment" }
  | { kind: "pathAxis"; index: number; axis: "x" | "y" | "z" }
  | { kind: "pathBend"; index: number; side: 1 | -1 }
  | { kind: "pathPoint"; index: number }
  | { kind: "pathMovePoint"; index: number }
  | { kind: "pathBendNew"; index: number }
  | { kind: "pathEmpty"; alt: boolean }
  | { kind: "pathObserve" }

export type ClassifyPlatformDownCtx = {
  button: number
  shift: boolean
  canEdit: boolean
  playing: boolean
  deviceLookActive: boolean
  /** Armed flythrough: drag looks, a click does not select or edit. */
  recordArmed: boolean
  /** Recording in progress: same look-only rule after arm is cleared. */
  recording: boolean
  /** Camera pose is changing (fly, playback, view animation). Click must not select. */
  cameraMoving: boolean
  hasSectorSelection: boolean
  hasFaceSelection: boolean
  meshHit: PlatformMeshHit
  cell: PlatformPointerCell | null
  faceKey?: string
  tile?: { faceKey: string; u: number; v: number }
  objectWallFaceKey?: string | null
  /** Scene object under the cursor. Click selects it; a drag looks instead. */
  objectUid?: string | null
  trajectory?: TrajectoryPointerCtx
}

export type ClassifyPlatformDownResult = {
  pending: PlatformPending | null
  /** Engine starts fly look when true (may also start look later on idle move). */
  wantLook: boolean
  capture: boolean
  previewAnchor: PlatformPointerCell | null
  pathDown?: PathDownEffect
}

export type PlatformMoveEffect =
  | { type: "none"; pending: PlatformPending | null }
  | { type: "cancelActivate"; pending: null }
  | { type: "idleStartLook"; pending: PlatformPending }
  | {
      type: "promoteMarquee"
      pending: null
      paint: { anchor: PlatformPointerCell; current: PlatformPointerCell; shift: boolean }
      prelude: boolean
    }
  | { type: "pathMovePoint"; pending: Extract<PlatformPending, { kind: "pathMovePoint" }> }
  | { type: "pathBend"; pending: Extract<PlatformPending, { kind: "pathBend" }> }
  | { type: "pathLook"; pending: null }

export type PlatformUpEffect =
  | { type: "clear" }
  | { type: "sectors"; sectors: PlatformPointerCell[]; additive: boolean }
  | { type: "activate"; target: PlatformActivateTarget; shift: boolean }
  | { type: "activateEnvironment" }
  | { type: "noop" }
  | { type: "pathSelect"; index: number }
  | { type: "pathCommit" }
  | { type: "pathPlaced"; index: number }
  | { type: "pathInsert" }
  | { type: "pathPick" }

/** True void: no platform mesh, no plane cell, no object-wall face. */
export function isPlatformVoidHit(args: {
  meshHit: PlatformMeshHit
  cell: PlatformPointerCell | null
  objectWallFaceKey?: string | null
}): boolean {
  if (args.meshHit !== "miss") return false
  if (args.cell != null) return false
  if (args.objectWallFaceKey) return false
  return true
}

export function classifyPlatformDown(ctx: ClassifyPlatformDownCtx): ClassifyPlatformDownResult {
  const empty: ClassifyPlatformDownResult = {
    pending: null,
    wantLook: false,
    capture: false,
    previewAnchor: null,
  }
  if (ctx.button !== 0 || ctx.playing || ctx.deviceLookActive) return empty
  // Движение камеры гасит клик, но точка, ось и рычаг траектории остаются жестом.
  if (
    ctx.shift &&
    !ctx.recordArmed &&
    !ctx.recording &&
    ctx.trajectory?.active &&
    isTrajectoryControlHit(ctx.trajectory.hit)
  ) {
    return classifyTrajectoryDown(ctx.trajectory)
  }
  if (ctx.cameraMoving || ctx.recordArmed || ctx.recording) {
    return { pending: null, wantLook: true, capture: false, previewAnchor: null }
  }
  if (ctx.trajectory?.active) return classifyTrajectoryDown(ctx.trajectory)

  if (ctx.objectUid) {
    return {
      pending: {
        kind: "activate",
        target: { kind: "object", uid: ctx.objectUid },
        shift: ctx.shift,
      },
      wantLook: true,
      capture: false,
      previewAnchor: null,
    }
  }

  const wallFace = ctx.meshHit === "face" || ctx.meshHit === "opening" ? ctx.faceKey : (ctx.objectWallFaceKey ?? null)

  if (ctx.hasSectorSelection) {
    if (wallFace) {
      const target: PlatformActivateTarget =
        ctx.meshHit === "opening" && ctx.tile
          ? { kind: "face", faceKey: ctx.tile.faceKey, tile: ctx.tile }
          : { kind: "face", faceKey: wallFace }
      return {
        pending: { kind: "activate", target, shift: ctx.shift },
        wantLook: true,
        capture: false,
        previewAnchor: null,
      }
    }
    if (ctx.cell) {
      return {
        pending: { kind: "sectorPaint", anchor: ctx.cell, shift: ctx.shift },
        wantLook: false,
        capture: true,
        previewAnchor: ctx.cell,
      }
    }
    return {
      pending: { kind: "idleClear" },
      wantLook: false,
      capture: true,
      previewAnchor: null,
    }
  }

  // Face/wall tool active, no wall/sector mesh (surface pending already handled): clear on up.
  if (ctx.hasFaceSelection && !wallFace && ctx.meshHit === "miss") {
    return {
      pending: { kind: "idleClear" },
      wantLook: false,
      capture: true,
      previewAnchor: null,
    }
  }

  if (!ctx.canEdit) {
    return { pending: null, wantLook: true, capture: false, previewAnchor: null }
  }

  if (ctx.meshHit === "sector" && ctx.cell) {
    return {
      pending: {
        kind: "activate",
        target: { kind: "sector", x: ctx.cell.x, z: ctx.cell.z },
        shift: ctx.shift,
      },
      wantLook: true,
      capture: false,
      previewAnchor: null,
    }
  }
  if (ctx.meshHit === "face" && ctx.faceKey) {
    return {
      pending: {
        kind: "activate",
        target: { kind: "face", faceKey: ctx.faceKey },
        shift: ctx.shift,
      },
      wantLook: true,
      capture: false,
      previewAnchor: null,
    }
  }
  if (ctx.meshHit === "opening" && ctx.faceKey) {
    const tile = ctx.tile ?? { faceKey: ctx.faceKey, u: 0, v: 0 }
    return {
      pending: {
        kind: "activate",
        target: { kind: "face", faceKey: ctx.faceKey, tile },
        shift: ctx.shift,
      },
      wantLook: true,
      capture: false,
      previewAnchor: null,
    }
  }
  if (wallFace) {
    return {
      pending: {
        kind: "activate",
        target: { kind: "face", faceKey: wallFace },
        shift: ctx.shift,
      },
      wantLook: true,
      capture: false,
      previewAnchor: null,
    }
  }

  if (
    isPlatformVoidHit({
      meshHit: ctx.meshHit,
      cell: ctx.cell,
      objectWallFaceKey: ctx.objectWallFaceKey,
    })
  ) {
    return {
      pending: { kind: "activateEnvironment" },
      wantLook: true,
      capture: false,
      previewAnchor: null,
    }
  }

  return { pending: null, wantLook: true, capture: false, previewAnchor: null }
}

export function resolvePlatformMove(args: {
  pending: PlatformPending | null
  moved: boolean
  hasSectorSelection: boolean
  currentCell: PlatformPointerCell | null
}): PlatformMoveEffect {
  const { pending, moved, hasSectorSelection, currentCell } = args
  if (!pending || !moved) return { type: "none", pending }

  if (pending.kind === "activate") {
    if (
      pending.target.kind === "sector" ||
      pending.target.kind === "object" ||
      (pending.target.kind === "face" && !hasSectorSelection)
    ) {
      return { type: "cancelActivate", pending: null }
    }
    return { type: "none", pending }
  }

  if (pending.kind === "activateEnvironment") {
    return { type: "cancelActivate", pending: null }
  }

  if (pending.kind === "idleClear") {
    return { type: "idleStartLook", pending }
  }

  if (pending.kind === "sectorPaint") {
    const current = currentCell ?? pending.anchor
    return {
      type: "promoteMarquee",
      pending: null,
      paint: { anchor: pending.anchor, current, shift: pending.shift },
      prelude: !pending.shift,
    }
  }

  if (pending.kind === "pathPoint") {
    return { type: "pathMovePoint", pending: { kind: "pathMovePoint", index: pending.index } }
  }
  if (pending.kind === "pathBendNew") {
    return { type: "pathBend", pending: { kind: "pathBend", index: pending.index, side: 1 } }
  }
  if (pending.kind === "pathEmpty" || pending.kind === "pathObserve") {
    return { type: "pathLook", pending: null }
  }

  return { type: "none", pending }
}

export function resolvePlatformUp(args: {
  pending: PlatformPending | null
  moved: boolean
  hasSectorSelection: boolean
  cellAtUp: PlatformPointerCell | null
  cameraMoving?: boolean
}): PlatformUpEffect {
  const { pending, moved, hasSectorSelection, cellAtUp, cameraMoving } = args
  if (cameraMoving || !pending) return { type: "noop" }

  if (pending.kind === "idleClear") {
    return { type: "clear" }
  }

  if (pending.kind === "sectorPaint") {
    if (moved) return { type: "noop" }
    const cell = cellAtUp
    if (cell) {
      return { type: "sectors", sectors: [cell], additive: pending.shift }
    }
    return { type: "noop" }
  }

  if (pending.kind === "activateEnvironment") {
    if (moved) return { type: "noop" }
    return { type: "activateEnvironment" }
  }

  if (pending.kind === "activate") {
    if (moved) {
      if (hasSectorSelection && pending.target.kind === "face") {
        return { type: "activate", target: pending.target, shift: pending.shift }
      }
      return { type: "noop" }
    }
    return { type: "activate", target: pending.target, shift: pending.shift }
  }

  if (pending.kind === "pathPoint") {
    if (moved) return { type: "noop" }
    return { type: "pathSelect", index: pending.index }
  }
  if (pending.kind === "pathMovePoint" || pending.kind === "pathAxis" || pending.kind === "pathBend") {
    return { type: "pathCommit" }
  }
  if (pending.kind === "pathBendNew") {
    if (moved) return { type: "noop" }
    return { type: "pathPlaced", index: pending.index }
  }
  if (pending.kind === "pathEmpty") {
    if (moved || !pending.alt) return { type: "noop" }
    return { type: "pathInsert" }
  }
  if (pending.kind === "pathObserve") {
    if (moved) return { type: "noop" }
    return { type: "pathPick" }
  }

  return { type: "noop" }
}

function isTrajectoryControlHit(hit: PathPointerHit | null): boolean {
  return hit?.kind === "point" || hit?.kind === "axis" || hit?.kind === "handle"
}

function classifyTrajectoryDown(trajectory: TrajectoryPointerCtx): ClassifyPlatformDownResult {
  const held = (
    pending: PlatformPending | null,
    pathDown: PathDownEffect = { type: "none" },
  ): ClassifyPlatformDownResult => ({
    pending,
    wantLook: false,
    capture: true,
    previewAnchor: null,
    pathDown,
  })
  const hit = trajectory.hit
  if (trajectory.doubleClick && (hit?.kind === "point" || hit?.kind === "handle")) {
    return held(null, { type: "resetTangent", index: hit.index })
  }
  if (hit?.kind === "axis") return held({ kind: "pathAxis", index: hit.index, axis: hit.axis })
  if (hit?.kind === "handle") return held({ kind: "pathBend", index: hit.index, side: hit.side })
  if (hit?.kind === "point") return held({ kind: "pathPoint", index: hit.index })
  if (trajectory.observeArmed) return held({ kind: "pathObserve" })
  if (trajectory.alt || (trajectory.draw && trajectory.onCurve)) return held({ kind: "pathEmpty", alt: true })
  if (trajectory.draw && trajectory.floorHit) {
    return held({ kind: "pathBendNew", index: -1 }, { type: "placePoint" })
  }
  return held({ kind: "pathEmpty", alt: false })
}

type PathPending = Extract<
  PlatformPending,
  {
    kind: "pathAxis" | "pathBend" | "pathPoint" | "pathMovePoint" | "pathBendNew" | "pathEmpty" | "pathObserve"
  }
>

export function isPathPending(pending: PlatformPending | null): pending is PathPending {
  if (!pending) return false
  return (
    pending.kind === "pathAxis" ||
    pending.kind === "pathBend" ||
    pending.kind === "pathPoint" ||
    pending.kind === "pathMovePoint" ||
    pending.kind === "pathBendNew" ||
    pending.kind === "pathEmpty" ||
    pending.kind === "pathObserve"
  )
}
